import { useState } from 'react';
import { Star, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReviewForm({ productId, orderId, onReviewSubmitted }) {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (rating === 0) {
            toast.error('Veuillez sélectionner une note');
            return;
        }

        if (comment.trim().length < 10) {
            toast.error('Votre commentaire doit contenir au moins 10 caractères');
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');

            await axios.post(
                `${API_URL}/reviews/`,
                {
                    product_id: productId,
                    order_id: orderId,
                    rating,
                    comment: comment.trim()
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success('Merci pour votre avis !');
            setRating(0);
            setComment('');

            if (onReviewSubmitted) {
                onReviewSubmitted();
            }
        } catch (error) {
            console.error('Error submitting review:', error);
            const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'avis';
            toast.error(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card className="border-orange-100">
            <CardHeader className="bg-orange-50/50">
                <CardTitle className="text-lg">Laisser un avis</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Star Rating */}
                    <div>
                        <Label className="text-base font-semibold mb-3 block">
                            Votre note
                        </Label>
                        <div className="flex items-center gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => setRating(star)}
                                    onMouseEnter={() => setHoverRating(star)}
                                    onMouseLeave={() => setHoverRating(0)}
                                    className="transition-transform hover:scale-110 focus:outline-none"
                                >
                                    <Star
                                        className={`h-8 w-8 ${star <= (hoverRating || rating)
                                                ? 'fill-yellow-400 text-yellow-400'
                                                : 'text-gray-300'
                                            }`}
                                    />
                                </button>
                            ))}
                            {rating > 0 && (
                                <span className="ml-2 text-sm font-medium text-gray-700">
                                    {rating === 1 && 'Très mauvais'}
                                    {rating === 2 && 'Mauvais'}
                                    {rating === 3 && 'Moyen'}
                                    {rating === 4 && 'Bon'}
                                    {rating === 5 && 'Excellent'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Comment */}
                    <div>
                        <Label htmlFor="comment" className="text-base font-semibold mb-3 block">
                            Votre commentaire
                        </Label>
                        <Textarea
                            id="comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Partagez votre expérience avec ce produit/service..."
                            className="min-h-[120px] resize-none"
                            maxLength={1000}
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            {comment.length}/1000 caractères (minimum 10)
                        </p>
                    </div>

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        disabled={submitting || rating === 0 || comment.trim().length < 10}
                        className="w-full bg-orange-500 hover:bg-orange-600"
                    >
                        {submitting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Envoi en cours...
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4 mr-2" />
                                Publier mon avis
                            </>
                        )}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
}
